/**
 * scene-background-library (機構 M15) Task 3.1:
 * client-player から SceneBackground CRUD API と Scene.backgroundLabel 更新 API を
 * 呼び出すための軽量 fetch wrapper。
 *
 * 設計 doctrine:
 * - client-player は core-domain への dep を持たない (= memory `project_client_player_no_core_domain_dep`)、
 *   types は本 file 内で独自 declaration
 * - scene-summary-editorial.ts と同 pattern (= manual registration endpoint、 openapi-fetch 外)
 *
 * 設計対応:
 *   - design.md §API Contract (SceneBackground Routes)
 *   - requirements.md Req 1.5, 3.4
 */
import { ApiError, type ProblemDetail } from "./client";

// ─── 独自 type declaration (= core-domain dep なし) ─────────

export interface SceneBackgroundResponse {
  id: string;
  scenarioId: string;
  label: string;
  aliases?: string[];
  assetType?: "background" | null;
  scenarioSlug?: string | null;
  imageId?: string | null;
  fileName?: string | null;
  assetKey?: string | null;
  assetRef?: {
    type: "background";
    scenarioSlug: string;
    imageId: string;
    fileName: string;
  } | null;
  imageUrl: string;
  createdAt: string;
  updatedAt: string;
}

export interface SceneBackgroundAssetRefInput {
  type: "background";
  scenarioSlug: string;
  imageId?: string | undefined;
  fileName: string;
}

export interface SceneBackgroundAssetDraftResponse {
  id: string;
  backgroundId: string;
  scenarioId: string;
  assetType: "background";
  scenarioSlug: string;
  imageId: string;
  fileName: string;
  assetKey: string;
  imageUrl: string;
  status: string;
  createdAt: string;
  publishedAt: string | null;
}

export interface SceneBackgroundAssetVersionResponse {
  id: string;
  backgroundId: string;
  scenarioId: string;
  draftId: string | null;
  fromAssetKey: string | null;
  fromImageUrl: string;
  toAssetKey: string | null;
  toImageUrl: string;
  publishedAt: string;
  rolledBackAt: string | null;
}

// ─── 内部 helper ─────────

function fallbackProblem(status: number, statusText: string): ProblemDetail {
  return {
    type: "about:blank",
    title: "Request failed",
    status,
    detail: statusText !== "" ? statusText : "Unknown error",
  };
}

async function parseJsonOrThrow<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let problem: ProblemDetail;
    try {
      problem = (await response.json()) as ProblemDetail;
    } catch {
      problem = fallbackProblem(response.status, response.statusText);
    }
    throw new ApiError(response.status, problem);
  }
  return (await response.json()) as T;
}

// ─── client functions ─────────

/**
 * GET /api/v1/scenarios/:scenarioId/backgrounds — 認証不要。
 * リーダーのプレイ中にも呼ぶため creator 認証なし (= design.md §API Contract)。
 */
export async function listBackgrounds(
  baseUrl: string,
  scenarioId: string,
): Promise<SceneBackgroundResponse[]> {
  const response = await fetch(
    `${baseUrl}/scenarios/${scenarioId}/backgrounds`,
    {
      method: "GET",
      credentials: "include",
    },
  );
  return parseJsonOrThrow<SceneBackgroundResponse[]>(response);
}

/**
 * POST /api/v1/scenarios/:scenarioId/backgrounds — creator role 必須。
 * ラベル重複時は 409 ConflictError が返る (= req 2.2)。
 */
export async function createBackground(
  baseUrl: string,
  scenarioId: string,
  data: {
    label: string;
    aliases?: string[];
    imageUrl?: string;
    assetRef?: SceneBackgroundAssetRefInput;
  },
): Promise<SceneBackgroundResponse> {
  const response = await fetch(
    `${baseUrl}/author/scenarios/${scenarioId}/backgrounds`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      credentials: "include",
      body: JSON.stringify(data),
    },
  );
  return parseJsonOrThrow<SceneBackgroundResponse>(response);
}

/**
 * PATCH /api/v1/author/scenario-backgrounds/:backgroundId — creator role 必須。
 * label / imageUrl の部分更新。ラベル重複時は 409 ConflictError (= req 2.3)。
 */
export async function updateBackground(
  baseUrl: string,
  backgroundId: string,
  data: {
    label?: string;
    aliases?: string[];
    imageUrl?: string;
    assetRef?: SceneBackgroundAssetRefInput;
  },
): Promise<SceneBackgroundResponse> {
  const response = await fetch(
    `${baseUrl}/author/scenario-backgrounds/${backgroundId}`,
    {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      credentials: "include",
      body: JSON.stringify(data),
    },
  );
  return parseJsonOrThrow<SceneBackgroundResponse>(response);
}

/**
 * POST /api/v1/author/scenario-backgrounds/:backgroundId/asset-drafts — creator role 必須。
 * published reference を変えず、server-side draft として保存する。
 */
export async function createBackgroundAssetDraft(
  baseUrl: string,
  backgroundId: string,
  data: { assetRef: SceneBackgroundAssetRefInput },
): Promise<SceneBackgroundAssetDraftResponse> {
  const response = await fetch(
    `${baseUrl}/author/scenario-backgrounds/${backgroundId}/asset-drafts`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      credentials: "include",
      body: JSON.stringify(data),
    },
  );
  return parseJsonOrThrow<SceneBackgroundAssetDraftResponse>(response);
}

/**
 * POST /api/v1/author/scene-background-asset-drafts/:draftId/publish — creator role 必須。
 * draft を published reference に昇格し、previous reference を履歴に残す。
 */
export async function publishBackgroundAssetDraft(
  baseUrl: string,
  draftId: string,
): Promise<{
  background: SceneBackgroundResponse;
  version: SceneBackgroundAssetVersionResponse;
}> {
  const response = await fetch(
    `${baseUrl}/author/scene-background-asset-drafts/${draftId}/publish`,
    {
      method: "POST",
      credentials: "include",
    },
  );
  return parseJsonOrThrow<{
    background: SceneBackgroundResponse;
    version: SceneBackgroundAssetVersionResponse;
  }>(response);
}

/**
 * POST /api/v1/author/scenario-backgrounds/:backgroundId/rollback — creator role 必須。
 * versionId 省略時は最新 publish 履歴の previous reference に戻す。
 */
export async function rollbackBackgroundAsset(
  baseUrl: string,
  backgroundId: string,
  versionId?: string,
): Promise<SceneBackgroundResponse> {
  const query = versionId !== undefined ? `?versionId=${encodeURIComponent(versionId)}` : "";
  const response = await fetch(
    `${baseUrl}/author/scenario-backgrounds/${backgroundId}/rollback${query}`,
    {
      method: "POST",
      credentials: "include",
    },
  );
  return parseJsonOrThrow<SceneBackgroundResponse>(response);
}

/**
 * DELETE /api/v1/author/scenario-backgrounds/:backgroundId — creator role 必須。
 * 204 No Content 正常応答。存在しない id は 404。
 */
export async function deleteBackground(
  baseUrl: string,
  backgroundId: string,
): Promise<void> {
  const response = await fetch(
    `${baseUrl}/author/scenario-backgrounds/${backgroundId}`,
    {
      method: "DELETE",
      credentials: "include",
    },
  );
  if (!response.ok && response.status !== 204) {
    let problem: ProblemDetail;
    try {
      problem = (await response.json()) as ProblemDetail;
    } catch {
      problem = fallbackProblem(response.status, response.statusText);
    }
    throw new ApiError(response.status, problem);
  }
}

/**
 * PATCH /api/v1/author/scenes/:sceneId/background-label — creator role 必須。
 * backgroundLabel を set (string) / 解除 (null) する (= req 3.1, 3.2)。
 * 204 No Content 正常応答。
 */
export async function updateSceneBackgroundLabel(
  baseUrl: string,
  sceneId: string,
  backgroundLabel: string | null,
): Promise<void> {
  const response = await fetch(
    `${baseUrl}/author/scenes/${sceneId}/background-label`,
    {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ backgroundLabel }),
    },
  );
  if (!response.ok && response.status !== 204) {
    let problem: ProblemDetail;
    try {
      problem = (await response.json()) as ProblemDetail;
    } catch {
      problem = fallbackProblem(response.status, response.statusText);
    }
    throw new ApiError(response.status, problem);
  }
}
